import React, { useState, useRef, useEffect, useCallback } from 'react';
import MonthPicker from '../../molecules/profile/components/MonthPicker';
import { showToast } from '../../../utils/toastManager';
import '../../../styles/resume-editor/edit-experience-form.css';

interface Certification {
  uid: string;
  certification_name: string;
  organization_name?: string;
  issued_at?: string | null;
  expires_at?: string | null;
  credential_id?: string;
  description?: string;
}

interface EditCertificationFormProps {
  certification: Certification;
  onSave: (data: Certification) => void;
  onCancel: () => void;
  onDataChange?: (data: Certification) => void;
  focusField?: string | null;
  onFocusFieldHandled?: () => void;
}

export const EditCertificationForm: React.FC<EditCertificationFormProps> = ({
  certification,
  onSave,
  onCancel,
  onDataChange,
  focusField,
  onFocusFieldHandled
}) => {
  const [certificationName, setCertificationName] = useState('');
  const [organization, setOrganization] = useState('');
  const [issuedAt, setIssuedAt] = useState<Date | null>(null);
  const [showIssuedCalendar, setShowIssuedCalendar] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [showExpiresCalendar, setShowExpiresCalendar] = useState(false);
  const [credentialId, setCredentialId] = useState('');
  const [description, setDescription] = useState('');
  const [yearOnly, setYearOnly] = useState(false);
  const [hideDate, setHideDate] = useState(false);

  const certificationNameInputRef = useRef<HTMLInputElement>(null);
  const organizationInputRef = useRef<HTMLInputElement>(null);
  const credentialIdInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const loadedCertificationUidRef = useRef<string | null>(null);
  const prevDataRef = useRef<string>('');
  const onDataChangeRef = useRef(onDataChange);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    if (certification && certification.uid !== loadedCertificationUidRef.current) {
      loadedCertificationUidRef.current = certification.uid;
      setCertificationName(certification.certification_name || '');
      setOrganization(certification.organization_name || '');
      setIssuedAt(certification.issued_at ? new Date(certification.issued_at) : null);
      setExpiresAt(certification.expires_at ? new Date(certification.expires_at) : null);
      setCredentialId(certification.credential_id || '');
      setDescription(certification.description || '');
      setYearOnly(false);
      setHideDate(false);
      
      if (onDataChangeRef.current) {
        const initialCertification: Certification = {
          ...certification,
          certification_name: certification.certification_name || '',
          organization_name: certification.organization_name || '',
          issued_at: certification.issued_at || null,
          expires_at: certification.expires_at || null,
          credential_id: certification.credential_id || '',
          description: certification.description || ''
        };
        onDataChangeRef.current(initialCertification);
      }
    }
  }, [certification?.uid]);

  useEffect(() => {
    if (!focusField) return;

    const timer = setTimeout(() => {
      if (focusField === 'certification_name' && certificationNameInputRef.current) {
        certificationNameInputRef.current.focus();
        certificationNameInputRef.current.select();
      } else if (focusField === 'organization_name' && organizationInputRef.current) {
        organizationInputRef.current.focus();
        organizationInputRef.current.select();
      } else if (focusField === 'credential_id' && credentialIdInputRef.current) {
        credentialIdInputRef.current.focus();
        credentialIdInputRef.current.select();
      } else if (focusField === 'description' && descriptionTextareaRef.current) {
        descriptionTextareaRef.current.focus();
        descriptionTextareaRef.current.select();
      } else if (focusField === 'issued_at') {
        setShowIssuedCalendar(true);
      } else if (focusField === 'expires_at') {
        setShowExpiresCalendar(true);
      }

      onFocusFieldHandled?.();
    }, 150);

    return () => clearTimeout(timer);
  }, [focusField, onFocusFieldHandled]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ select date';
    if (yearOnly) {
      return date.getFullYear().toString();
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const createUpdatedCertification = (): Certification => {
    const updated: Certification = {
      ...certification,
      certification_name: certificationName,
      organization_name: organization || undefined,
      issued_at: hideDate ? null : (issuedAt ? issuedAt.toISOString() : null),
      expires_at: hideDate ? null : (expiresAt ? expiresAt.toISOString() : null),
      credential_id: credentialId || undefined,
      description: description || undefined
    };
    return updated;
  };

  const updatePreviewData = () => {
    if (onDataChangeRef.current) {
      const updated = createUpdatedCertification();
      const currentData = JSON.stringify({
        certificationName,
        organization,
        issuedAt: issuedAt?.toISOString(),
        expiresAt: expiresAt?.toISOString(),
        credentialId,
        description,
        hideDate,
        yearOnly
      });
      
      if (currentData !== prevDataRef.current) {
        prevDataRef.current = currentData;
        onDataChangeRef.current(updated);
      }
    }
  };

  useEffect(() => {
    const shouldUpdate = certification?.uid === 'new' || certification?.uid === loadedCertificationUidRef.current;
    if ((certificationName !== '' || certification) && shouldUpdate) {
      updatePreviewData();
    }
  }, [certificationName, organization, issuedAt, expiresAt, credentialId, description, hideDate, yearOnly, certification?.uid]);

  const handleSave = () => {
    if (!isFormValid) {
      showToast('Please enter a certification name.', 'error');
      return;
    }
    const updatedCertification = createUpdatedCertification();
    onSave(updatedCertification);
  };

  const isFormValid = certificationName.trim() !== '';

  const handleClearIssuedAt = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIssuedAt(null);
    setShowIssuedCalendar(false);
  };

  const handleClearExpiresAt = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpiresAt(null);
    setShowExpiresCalendar(false);
  };

  return (
    <div className="edit-experience-form">
      <div className="edit-experience-form-content">
        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Certification Name</span>
            <span style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'normal' }}>Required</span>
          </label>
          <input
            ref={certificationNameInputRef}
            type="text"
            className="edit-experience-form-input"
            value={certificationName}
            onChange={(e) => setCertificationName(e.target.value)}
            placeholder="Enter certification name"
          />
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Organization</label>
          <input
            ref={organizationInputRef}
            type="text"
            className="edit-experience-form-input"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            placeholder="Enter organization name"
          />
        </div>

        <div className="edit-experience-form-field">
          <div className="edit-experience-form-dates-row">
            <div className="edit-experience-form-date-wrapper">
              <label className="edit-experience-form-label">Issued at</label>
              <div className="edit-experience-form-input-wrapper" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="edit-experience-form-date-btn"
                  onClick={() => {
                    setShowIssuedCalendar(!showIssuedCalendar);
                    setShowExpiresCalendar(false);
                  }}
                  disabled={hideDate}
                  style={{ width: '100%', textAlign: 'left', paddingRight: issuedAt ? '28px' : '8px' }}
                >
                  {formatDate(issuedAt)}
                </button>
                {issuedAt && (
                  <button
                    type="button"
                    onClick={handleClearIssuedAt}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#9ca3af',
                      padding: '0',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
              {showIssuedCalendar && (
                <div className="edit-experience-form-month-picker-wrapper">
                  <MonthPicker
                    selectedDate={issuedAt}
                    onSelect={(date) => {
                      setIssuedAt(date);
                      setShowIssuedCalendar(false);
                    }}
                    onClose={() => setShowIssuedCalendar(false)}
                    maxDate={expiresAt || undefined}
                  />
                </div>
              )}
            </div>
            <div className="edit-experience-form-date-wrapper">
              <label className="edit-experience-form-label">Expires At</label>
              <div className="edit-experience-form-input-wrapper" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="edit-experience-form-date-btn"
                  onClick={() => {
                    setShowExpiresCalendar(!showExpiresCalendar);
                    setShowIssuedCalendar(false);
                  }}
                  disabled={hideDate}
                  style={{ width: '100%', textAlign: 'left', paddingRight: expiresAt ? '28px' : '8px' }}
                >
                  {formatDate(expiresAt)}
                </button>
                {expiresAt && (
                  <button
                    type="button"
                    onClick={handleClearExpiresAt}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#9ca3af',
                      padding: '0',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
              {showExpiresCalendar && (
                <div className="edit-experience-form-month-picker-wrapper edit-experience-form-month-picker-wrapper--end-date">
                  <MonthPicker
                    selectedDate={expiresAt}
                    onSelect={(date) => {
                      setExpiresAt(date);
                      setShowExpiresCalendar(false);
                    }}
                    onClose={() => setShowExpiresCalendar(false)}
                    minDate={issuedAt || undefined}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="edit-experience-form-date-toggles">
            <label className="edit-experience-form-toggle-label">
              <span>Year only</span>
              <div
                className={`edit-experience-form-toggle-switch ${yearOnly ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => {
                  setYearOnly(!yearOnly);
                  updatePreviewData();
                }}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
            <label className="edit-experience-form-toggle-label">
              <span>Hide</span>
              <div
                className={`edit-experience-form-toggle-switch ${hideDate ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => {
                  setHideDate(!hideDate);
                  updatePreviewData();
                }}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
          </div>
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Credential ID</label>
          <input
            ref={credentialIdInputRef}
            type="text"
            className="edit-experience-form-input"
            value={credentialId}
            onChange={(e) => setCredentialId(e.target.value)}
            placeholder="Enter credential ID"
          />
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Description</label>
          <textarea
            ref={descriptionTextareaRef}
            className="edit-experience-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            rows={3}
          />
        </div>
      </div>

      <div className="edit-experience-form-footer">
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--update"
          onClick={handleSave}
          disabled={!isFormValid}
        >
          {certification.uid === 'new' ? 'Add' : 'Update'}
        </button>
      </div>
    </div>
  );
};

